import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  /** @type {string | null} */
  #originalPriceContainerHTML = null;

  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    closestSection.addEventListener('theme:purchase-type-change', this.#handlePurchaseTypeChange);

    // Self-initialize from the rendered DOM in case the purchase-selector's
    // initial broadcast already fired before this element was upgraded.
    this.#syncToActivePurchaseOption();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    closestSection.removeEventListener('theme:purchase-type-change', this.#handlePurchaseTypeChange);
  }

  /**
   * Swap the displayed price when the purchase selector changes (or fires its
   * initial broadcast on load). For 'onetime' we restore the originally
   * server-rendered markup so MSRP cross-out and other price logic survives.
   * For 'subscribe' / '3pack' we drop in the formatted price text from the
   * active selector option.
   * @param {CustomEvent<{type: string, priceHTML: string, initial?: boolean}>} event
   */
  #handlePurchaseTypeChange = (event) => {
    const { type, priceHTML } = event.detail || {};
    this.#applyPurchaseType(type, priceHTML);
  };

  /**
   * Read the currently-active purchase-selector option in this section (if any)
   * and apply its price markup. Called once on connect so the initial render
   * matches the authored default even if the broadcast event was missed.
   */
  #syncToActivePurchaseOption() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    const activeOpt = closestSection.querySelector('.purchase-selector__option--active');
    if (!activeOpt) return;
    const input = activeOpt.querySelector('.purchase-selector__input');
    const type = input instanceof HTMLInputElement ? input.value : 'onetime';
    if (type === 'onetime') return;
    const priceEl = activeOpt.querySelector('.purchase-selector__price');
    const priceHTML = priceEl ? priceEl.innerHTML : '';
    this.#applyPurchaseType(type, priceHTML);
  }

  /**
   * Swap the displayed price for a given purchase type.
   * For 'onetime' we restore the originally server-rendered markup so MSRP
   * cross-out and other price logic survives. For 'subscribe' / '3pack' we
   * drop in the formatted price text from the active selector option.
   * @param {string | undefined} type
   * @param {string | undefined} priceHTML
   */
  #applyPurchaseType(type, priceHTML) {
    const { priceContainer } = this.refs;
    if (!priceContainer) return;

    if (this.#originalPriceContainerHTML == null) {
      this.#originalPriceContainerHTML = priceContainer.innerHTML;
    }

    if (type === 'onetime' || !priceHTML) {
      priceContainer.innerHTML = this.#originalPriceContainerHTML;
      return;
    }

    priceContainer.innerHTML = `<span class="price">${priceHTML}</span>`;
  }

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const { priceContainer, volumePricingNote } = this.refs;
    // Find the new product-price element in the updated HTML
    const newProductPrice = event.detail.data.html.querySelector(
      `product-price[data-block-id="${this.dataset.blockId}"]`
    );
    if (!newProductPrice) return;

    // Update price container
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    if (newPrice && priceContainer) {
      priceContainer.replaceWith(newPrice);
    }

    // Update volume pricing note
    const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');

    if (!newNote) {
      volumePricingNote?.remove();
    } else if (!volumePricingNote) {
      // Use newPrice since priceContainer was just replaced and now points to the detached element
      newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      volumePricingNote.replaceWith(newNote);
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
